import * as React from 'react';
import { forwardRef, MouseEventHandler, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { LinkIcon } from '@heroicons/react/24/outline';

import { Button, Tooltip, Switch } from '@restworld/ui-ds-base';

import { cn } from '@restworld/utils-common';
/* eslint-disable-next-line */
export interface ReferenceRequestCardProps {
  role: string;
  venueName: string;
  fromDate: string;
  toDate?: string;
  handleShareLink?: MouseEventHandler<HTMLButtonElement>;
  isShareable?: boolean;
  disabled?: boolean;
  disableShareLink?: boolean;
  showTooltip?: boolean;
}

const CardContainer = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'w-full max-w-full h-fit px-3 py-4 bg-neutral-10 rounded-2xl border border-neutral-40 inline-flex flex-col justify-start items-start gap-4',
        className
      )}
      {...props}
    />
  )
);

const TopRow = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('self-stretch inline-flex justify-start items-start gap-2', className)} {...props} />
);

const InfoCol = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-[78%] flex-1 inline-flex flex-col justify-start items-start gap-1', className)} {...props} />
);

const RoleText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5 className={cn('justify-start text-neutral-700 text-base font-bold', className)} {...props} />
);

const VenueText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5 className={cn('justify-start text-neutral-700 text-base font-bold w-full truncate', className)} {...props} />
);

const DateText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5 className={cn('justify-start text-neutral-200 text-sm font-normal', className)} {...props} />
);

export const ReferenceRequestCard = forwardRef<HTMLDivElement, ReferenceRequestCardProps>(
  (
    {
      handleShareLink,
      role,
      venueName,
      fromDate,
      toDate,
      isShareable,
      disabled,
      disableShareLink,
      showTooltip,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation('worker-references-page');
    const [checked, setChecked] = useState(false);
    return (
      <CardContainer ref={ref}>
        <TopRow>
          <InfoCol>
            <RoleText>
              {t('reference_request_card.role_at', {
                role,
              })}
            </RoleText>
            <VenueText>{venueName}</VenueText>
          </InfoCol>
          <Switch disabled={disabled} size="sm" checked={checked} onChange={setChecked} {...rest} />
        </TopRow>
        <DateText>
          {t('reference_request_card.date_title', {
            fromDate,
            toDate,
          })}
        </DateText>
        {isShareable &&
          (() => {
            const shareButton = (
              <Button
                disabled={disableShareLink}
                fullwidth
                variant="secondary-restworld"
                StartIcon={LinkIcon}
                onClick={handleShareLink}
              >
                {t('reference_request_card.actions.primary')}
              </Button>
            );
            return showTooltip ? (
              <Tooltip content={t('reference_request_card.actions.tooltip')} className={'w-full'}>
                {shareButton}
              </Tooltip>
            ) : (
              shareButton
            );
          })()}
      </CardContainer>
    );
  }
);

export default ReferenceRequestCard;
